import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Collapse from 'react-bootstrap/Collapse';
import { WebOrderStatistics } from '@api/shipping-data/types/WebOrderStatistics';
import { DeliveryStatus } from '@api/shipping-data/types/DeliveryStatus';
import { setSelectedOrderUnvId } from '@pages/orders/store/slice';
import { OrderDates } from 'components/order-elements/OrderDates';
import { OrderTimes } from 'components/order-elements/OrderTimes';
import { OrderIcons } from 'components/order-elements/OrderIcons';
import { InfobarValues, Infobar } from 'components/order-elements/Infobar';
import Detail from '@pages/orders/detail/Detail';
import { GoBackIcon } from '@pages/orders/orders-page/list-area/order-item/GoBackIcon';



interface OrderItemProps {
	item: WebOrderStatistics,
	dateWithoutTime: string,
	isSmallScreen: boolean,
	isSelected: boolean,
	gobackReportingUrl: string | null,
}


const OrderItem = (props: OrderItemProps) => {
	const { dateWithoutTime, isSmallScreen, isSelected, gobackReportingUrl } = props;
	const { orderStatistics, destination } = props.item;

	const orderUnvId = orderStatistics && orderStatistics.unversionedSystemId ? orderStatistics.unversionedSystemId : '-';
	const orderClientId = orderStatistics && orderStatistics.clientId ? orderStatistics.clientId : '-';
	const companyName = destination && destination.companyName ? destination.companyName : '-';
	const destinationClientId = destination && destination.clientId ? destination.clientId : '-';
	const customerClassification = destination && destination.customerClassification ? destination.customerClassification : '-';
	const address = destination && destination.addressess && destination.addressess.length > 0 ? destination.addressess[0] : null;
	const country = destination && destination.country ? destination.country : '';
	const tradeCode = destination && destination.district && destination.district.sdName ? destination.district.sdName : '-';
	const distributionCode = orderStatistics && orderStatistics.shipmentDistributionCode ? orderStatistics.shipmentDistributionCode : '-';
	const originalDeliveryDate = orderStatistics && orderStatistics.orderOriginalRequiredDeliveryDate ? orderStatistics.orderOriginalRequiredDeliveryDate : null;
	const deliveryDate = orderStatistics && orderStatistics.orderRequiredDeliveryDate ? orderStatistics.orderRequiredDeliveryDate : null;
	const plannedArrival = orderStatistics && orderStatistics.shipmentPlannedArrival ? orderStatistics.shipmentPlannedArrival : null;
	const etaArrival = orderStatistics && orderStatistics.shipmentEtaArrival ? orderStatistics.shipmentEtaArrival : null;
	const realArrival = orderStatistics && orderStatistics.deliveryRealArrival ? orderStatistics.deliveryRealArrival : null;
	const smsWasSent = orderStatistics ? orderStatistics.smsWasSent : false;
	const deliveryStatus = orderStatistics && orderStatistics.deliveryStatus ? orderStatistics.deliveryStatus : DeliveryStatus.none;
	const existDelivery = Boolean(orderStatistics && orderStatistics.deliveryUnversionedSystemId);
	const existShipment = Boolean(orderStatistics && orderStatistics.shipmentUnversionedSystemId);
	const isRedated = orderStatistics ? orderStatistics.orderRedated : false;
	const isReduced = orderStatistics ? orderStatistics.orderReduced : false;
	const isCancelled = orderStatistics ? orderStatistics.orderCancelled : false;
	const isDelivered = orderStatistics && orderStatistics.deliveryStatus ? orderStatistics.deliveryStatus === DeliveryStatus.delivered : false;
	const arrivalsDeltaInMinutes = orderStatistics && orderStatistics.deliveryDateDeviationInMinutes ? orderStatistics.deliveryDateDeviationInMinutes : 0;
	const containsBeer = orderStatistics ? orderStatistics.containsBeer : false;
	const containsTank = orderStatistics ? orderStatistics.containsTank : false;
	const containsFreeBeer = orderStatistics ? orderStatistics.containsFreeBeer : false;
	const containsCollection = orderStatistics ? orderStatistics.containsCollection : false;
	const containsEmpties = orderStatistics ? orderStatistics.containsEmpties : false;
	const containsPosm = orderStatistics ? orderStatistics.containsPosm : false;
	const containsClaim = orderStatistics ? orderStatistics.containsClaim : false;
	const containsUnknown = orderStatistics ? orderStatistics.containsUnknown : false;
	const countValues: InfobarValues = {
		value1: orderStatistics?.orderItemsCount,
		showExclamation1: Boolean(orderStatistics?.orderReduced),
		value2: orderStatistics?.deliveryItemsCount,
		showExclamation2: Boolean(orderStatistics?.deliveryReducedDueToPpas),
		value3: orderStatistics?.deliveredDeliveryItemsCount,
		showExclamation3: Boolean(orderStatistics?.deliveredDeliveryReducedDueToPpas),
	};
	const volumeValues: InfobarValues = {
		value1: orderStatistics?.orderItemsVolume,
		showExclamation1: Boolean(orderStatistics?.orderReduced),
		value2: orderStatistics?.deliveryItemsVolume,
		showExclamation2: Boolean(orderStatistics?.deliveryReducedDueToPpas),
		value3: orderStatistics?.deliveredDeliveryItemsVolume,
		showExclamation3: Boolean(orderStatistics?.deliveredDeliveryReducedDueToPpas),
	};

	const dispatch = useDispatch();
	const runSetSelectedOrderUnvId = (orderUnvId: string | null) => dispatch(setSelectedOrderUnvId(orderUnvId));

	const timerRef = useRef<NodeJS.Timeout>(null);
	const [ open, setOpen ] = useState(false);

	
	useEffect(() => {
		if (open && !isSelected) {
			setOpen(false);
		}
	}, [ isSelected ]);

	
	const handleClick = useCallback(() => {
		if (!isSmallScreen) {
			runSetSelectedOrderUnvId(orderUnvId);
		}
		if (isSmallScreen && !timerRef.current) {
			timerRef.current = setTimeout(() => {
				if (timerRef.current) {
					timerRef.current = null;
				}
			}, 300);
			setOpen(!open);
		}
	},[ timerRef.current, orderUnvId, open, isSmallScreen ]);


	return (
		<div>
			<div className={`order-item${isSelected ? ' selected-order' : ''}${
					containsEmpties ? ' empties-order'
					: containsCollection ? ' collection-order'
					: containsFreeBeer ? ' freebeer-order'
					: ''}`}
				id={`order-client-id:${orderClientId}`}
				onClick={handleClick}>

				<p className="order-name">{companyName !== '-' ? `${companyName} (Obj. č. ${orderClientId})` : `Obj. č. ${orderClientId}`}</p>
				<div className="order-category">
					<GoBackIcon gobackUrl={gobackReportingUrl} />
					<p>{destinationClientId}</p>
					<p className="category-icon">{customerClassification}</p>
				</div>
				
				
				<p className="order-street">{address && address.street ? address.street : '-'}</p>
				<p className="order-city">{address && address.city ? address.city : '-'}</p>
				<p className="order-zip">{address ?
					country ? `${country} ${address.zipCode}` : address.zipCode
					: '-'}</p>
				
				
				<OrderDates
					originalDeliveryDate={originalDeliveryDate}
					deliveryDate={deliveryDate}
					smsWasSent={smsWasSent}
					isRedated={isRedated} />
				<p className="order-direct">{tradeCode}</p>
				<p className="order-distribution-type">{distributionCode}</p>
				
				
				<div className="order-status">
					<OrderIcons 
						existDelivery={existDelivery}
						existShipment={existShipment}
						isRedated={isRedated}
						isCancelled={isCancelled}
						isReduced={isReduced}
						isDelivered={isDelivered}
						smsWasSend={smsWasSent}
					/>
					<OrderTimes 
						plannedArrival={plannedArrival}
						realArrival={realArrival}
						etaArrival={etaArrival}
						deliveryStatus={deliveryStatus}
						smsWasSent={smsWasSent}
						arrivalsDeltaInMinutes={arrivalsDeltaInMinutes}
					/>
				</div>
				<Infobar
					containsBeer={containsBeer}
					containsTank={containsTank}
					containsFreeBeer={containsFreeBeer}
					containsCollection={containsCollection}
					containsEmpties={containsEmpties}
					containsPosm={containsPosm}
					containsClaim={containsClaim}
					containsUnknown={containsUnknown}
					isCancelled={isCancelled}
					isReduced={isReduced}
					isDelivered={isDelivered}
					countValues={countValues}
					volumeValues={volumeValues}
				/>

			</div>
			{isSmallScreen &&
				<Collapse in={open}
					onEnter={() => runSetSelectedOrderUnvId(orderUnvId)}
					onExited={() => runSetSelectedOrderUnvId(null)}
					>
					<div>
						<Detail
							dateWithoutTime={dateWithoutTime}
							isSmallScreen={isSmallScreen} />
					</div>
				</Collapse>}
		</div>
	)
}

export default OrderItem;